import Link from 'next/link';
import styled, { css, keyframes } from 'styled-components';
import Image from '@/components/image';
import { Muted, SectionContainer } from '@/components/ui';
import { up } from '@/themes';
import { getFallbackImage } from '@/utils/images';
import { brandHref } from './brand-href';

// Below this many logos a row cannot fill the page width, so nothing scrolls.
const MIN_ANIMATED = 12;

const scroll = keyframes`
  to {
    transform: translate3d(-50%, 0, 0);
  }
`;

const Outer = styled.section`
  padding: 24px 0;

  ${up.sm} {
    padding: 32px 0;
  }
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${({ $animated }) =>
    $animated &&
    css`
      --fade: 40px;

      -webkit-mask-image: linear-gradient(
        to right,
        transparent,
        black var(--fade),
        black calc(100% - var(--fade)),
        transparent
      );
      mask-image: linear-gradient(
        to right,
        transparent,
        black var(--fade),
        black calc(100% - var(--fade)),
        transparent
      );

      ${up.sm} {
        --fade: 15%;
      }

      @media (prefers-reduced-motion: reduce) {
        -webkit-mask-image: none;
        mask-image: none;
      }
    `};
`;

const Row = styled.div`
  display: flex;
  overflow: hidden;
  /* Room for the logos' focus ring and hover lift inside the clipped row. */
  padding: 4px 0;
`;

const Group = styled.ul`
  display: flex;
  flex-shrink: 0;
  list-style: none;
  margin: 0;
  padding: 0;
`;

/* Each group is rendered twice; sliding the track by half its width loops. */
const Track = styled.div`
  display: flex;

  ${({ $animated, $reverse }) =>
    $animated
      ? css`
          animation: ${scroll} 60s linear infinite ${$reverse ? 'reverse' : 'normal'};
          width: max-content;

          ${Rows}:hover &,
          ${Rows}:focus-within & {
            animation-play-state: paused;
          }

          /* Reduced motion: a static, wrapped, centred list. */
          @media (prefers-reduced-motion: reduce) {
            animation: none;
            width: 100%;

            ${Group} {
              flex-wrap: wrap;
              justify-content: center;
              row-gap: 12px;
              width: 100%;
            }

            ${Group}[aria-hidden='true'] {
              display: none;
            }
          }
        `
      : css`
          width: 100%;

          ${Group} {
            flex-wrap: wrap;
            justify-content: center;
            row-gap: 12px;
            width: 100%;
          }
        `};
`;

const Item = styled.li`
  flex-shrink: 0;
  padding: 0 6px;

  ${up.sm} {
    padding: 0 8px;
  }
`;

const LogoLink = styled(Link)`
  align-items: center;
  /* A brand's own backing colour: the same in both themes. */
  background-color: ${({ $backgroundColor }) => $backgroundColor || '#ffffff'};
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  display: flex;
  height: 60px;
  justify-content: center;
  padding: 10px 14px;
  transition:
    border-color 200ms,
    box-shadow 200ms;
  width: 100px;

  ${up.sm} {
    height: 72px;
    padding: 12px 16px;
    width: 120px;
  }

  &:hover {
    border-color: color-mix(in oklab, var(--primary) 50%, transparent);
    box-shadow: var(--shadow-md);
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 2px;
  }

  picture {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
  }

  img {
    height: auto;
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    width: auto;
  }
`;

const Trademark = styled(Muted)`
  font-size: 12px;
  line-height: 16px;
  margin: 20px auto 0;
  max-width: 768px;
  text-align: center;
`;

const Logos = ({ brands, duplicate }) => (
  <Group aria-hidden={duplicate ? 'true' : undefined}>
    {brands.map((brand) => (
      <Item key={brand.slug}>
        <LogoLink
          $backgroundColor={brand.backgroundColor}
          href={brandHref(brand)}
          tabIndex={duplicate ? -1 : undefined}
        >
          <Image
            alt={duplicate ? '' : brand.name}
            fallback={getFallbackImage('giftcard')}
            src={brand.imageUrl}
            unoptimized
          />
        </LogoLink>
      </Item>
    ))}
  </Group>
);

/**
 * Two rows of brand logos sliding in opposite directions, in CSS only. The
 * rows fade out at the edges, pause on hover or focus, and become a static
 * wrapped list for visitors who prefer reduced motion. Every logo links to
 * its brand page; the looping copy is hidden from assistive technology.
 */
const LogoMarquee = ({ brands = [], label, trademark }) => {
  if (brands.length === 0) return null;

  const animated = brands.length >= MIN_ANIMATED;
  const half = Math.ceil(brands.length / 2);
  const rows = animated ? [brands.slice(0, half), brands.slice(half)] : [brands];

  return (
    <Outer aria-label={label} data-section="brands">
      <SectionContainer>
        <Rows $animated={animated}>
          {rows.map((row, index) => (
            <Row key={row[0].slug}>
              <Track $animated={animated} $reverse={index % 2 === 1}>
                <Logos brands={row} />
                {animated && <Logos brands={row} duplicate />}
              </Track>
            </Row>
          ))}
        </Rows>
        {trademark && <Trademark>{trademark}</Trademark>}
      </SectionContainer>
    </Outer>
  );
};

export default LogoMarquee;
